import React, { useState } from 'react';
import './LegalPage.css';

const LAST_UPDATED = 'March 2026';
const CONTACT_EMAIL = 'hello.chopmyshop@gmail.com';

// ── Quick summary items ────────────────────────────────────────────────────────
const QUICK_SUMMARY = [
  { text: 'We don\'t sell your personal data.' },
  { text: 'We use your data to improve price accuracy.' },
  { text: 'You control your account and can request deletion.' },
  { text: 'Prices may not always be perfect — we\'re improving constantly.' },
];

// ── Privacy Policy content ─────────────────────────────────────────────────────
function PrivacyContent() {
  return (
    <div className="legal-doc">
      <p className="legal-doc-intro">
        We built ChopMyShop to help people make smarter shopping decisions. To do that,
        we collect a small amount of data — and we're clear about how and why.
      </p>

      <section className="legal-section">
        <h2 className="legal-h2">1. What we collect</h2>
        <h3 className="legal-h3">Account information</h3>
        <ul className="legal-list"><li>Email address (if you create an account)</li></ul>
        <h3 className="legal-h3">Shopping activity</h3>
        <ul className="legal-list">
          <li>Products you add to your lists</li>
          <li>Stores you compare</li>
          <li>Preferences (e.g. favourite supermarkets)</li>
        </ul>
        <h3 className="legal-h3">User contributions</h3>
        <ul className="legal-list">
          <li>Price reports</li>
          <li>Receipt uploads (if used)</li>
          <li>Feedback you submit</li>
        </ul>
        <h3 className="legal-h3">Technical data</h3>
        <ul className="legal-list">
          <li>Device type, app usage, and basic analytics to improve performance</li>
        </ul>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">2. How we use your data</h2>
        <p>We use your data to:</p>
        <ul className="legal-list">
          <li>Show you relevant price comparisons</li>
          <li>Improve accuracy of prices and product matching</li>
          <li>Understand how people use the app so we can improve it</li>
          <li>Review and validate user-submitted price data</li>
        </ul>
        <p className="legal-emphasis">We do not use your data for advertising profiling.</p>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">3. Crowdsourced data</h2>
        <p>Some features rely on user contributions (like price updates or receipts).</p>
        <ul className="legal-list">
          <li>These are reviewed before being used</li>
          <li>We may aggregate this data to improve accuracy</li>
          <li>We do not publicly show personal information linked to submissions</li>
        </ul>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">4. Data sharing</h2>
        <p className="legal-emphasis">We do not sell your personal data.</p>
        <p>
          We may use trusted third-party services (e.g. hosting, analytics) to run the app,
          but they only process data on our behalf.
        </p>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">5. Data storage &amp; retention</h2>
        <ul className="legal-list">
          <li>Your data is stored securely</li>
          <li>We keep data only as long as necessary to operate and improve the app</li>
          <li>You can request deletion of your account and associated data at any time</li>
        </ul>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">6. Your rights</h2>
        <p>You can:</p>
        <ul className="legal-list">
          <li>Request access to your data</li>
          <li>Request correction or deletion</li>
          <li>Contact us with any concerns</li>
        </ul>
        <p>Email: <a className="legal-link" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a></p>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">7. Accuracy disclaimer</h2>
        <p>We aim to provide accurate, up-to-date pricing, but:</p>
        <ul className="legal-list">
          <li>Prices may change</li>
          <li>Not all stores or products are always up to date</li>
        </ul>
        <p>We encourage users to treat the app as a guide, not a guarantee.</p>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">8. Updates to this policy</h2>
        <p>
          We may update this policy as the product evolves.
          We'll notify you of significant changes.
        </p>
      </section>

      <p className="legal-contact">
        Questions? Contact us at{' '}
        <a className="legal-link" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
      </p>
    </div>
  );
}

// ── Terms of Use content ───────────────────────────────────────────────────────
function TermsContent() {
  return (
    <div className="legal-doc">
      <p className="legal-doc-intro">
        By using ChopMyShop, you agree to the following.
      </p>

      <section className="legal-section">
        <h2 className="legal-h2">1. Purpose of the app</h2>
        <p>ChopMyShop helps you compare grocery prices and plan your shopping.</p>
        <p>It is an informational tool — not a retailer, and not responsible for final purchase prices.</p>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">2. Accuracy of information</h2>
        <p>We aim for high accuracy, but:</p>
        <ul className="legal-list">
          <li>Prices may change</li>
          <li>Availability may vary by store and location</li>
        </ul>
        <p>You should always verify key details before purchasing.</p>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">3. User contributions</h2>
        <p>If you submit prices, receipts, or feedback:</p>
        <ul className="legal-list">
          <li>You agree the information is accurate to the best of your knowledge</li>
          <li>You allow us to use that data to improve the platform</li>
        </ul>
        <p>We reserve the right to remove or ignore submissions.</p>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">4. Fair use</h2>
        <p>You agree not to:</p>
        <ul className="legal-list">
          <li>Misuse the platform (e.g. spam, false data submissions)</li>
          <li>Attempt to disrupt or manipulate pricing data</li>
        </ul>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">5. Accounts</h2>
        <p>You are responsible for:</p>
        <ul className="legal-list">
          <li>Keeping your account details secure</li>
          <li>Any activity under your account</li>
        </ul>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">6. Changes to the service</h2>
        <p>We may:</p>
        <ul className="legal-list">
          <li>Update features</li>
          <li>Add or remove functionality</li>
          <li>Improve how pricing and comparisons work</li>
        </ul>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">7. Limitation of liability</h2>
        <p>ChopMyShop is provided as-is. We are not liable for:</p>
        <ul className="legal-list">
          <li>Pricing inaccuracies</li>
          <li>Shopping decisions made using the app</li>
          <li>Availability of products in stores</li>
        </ul>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">8. Updates to these terms</h2>
        <p>
          We may update these terms as the product evolves.
          Continued use means you accept any updates.
        </p>
      </section>

      <p className="legal-contact">
        Questions? Contact us at{' '}
        <a className="legal-link" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
      </p>
    </div>
  );
}

// ── Main LegalPage ─────────────────────────────────────────────────────────────
export default function LegalPage({ onBack }) {
  const [view, setView] = useState(null); // null | 'privacy' | 'terms'
  const [openSummary, setOpenSummary] = useState(true);

  // ── Document view ──────────────────────────────────────────────────────────
  if (view) {
    const isPrivacy = view === 'privacy';
    return (
      <div className="legal-page">
        <div className="legal-doc-header">
          <button className="legal-back-btn" onClick={() => setView(null)} aria-label="Back">
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="13 18 7 12 13 6"/>
            </svg>
          </button>
          <div className="legal-doc-header-text">
            <span className="legal-doc-title">{isPrivacy ? 'Privacy Policy' : 'Terms of Use'}</span>
            <span className="legal-doc-updated">Last updated: {LAST_UPDATED}</span>
          </div>
        </div>
        <div className="legal-doc-body">
          {isPrivacy ? <PrivacyContent /> : <TermsContent />}
        </div>
      </div>
    );
  }

  // ── Hub view ───────────────────────────────────────────────────────────────
  return (
    <div className="legal-page">
      <div className="legal-hub-header">
        <button className="legal-back-btn" onClick={onBack} aria-label="Back">
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="13 18 7 12 13 6"/>
          </svg>
        </button>
        <div className="legal-doc-header-text">
          <span className="legal-doc-title">Privacy &amp; Terms</span>
        </div>
      </div>

      <div className="legal-hub-body">
        <p className="legal-hub-intro">
          We take your data seriously. Here's what we collect, why we collect it, and how we use it.
        </p>

        {/* Two document cards */}
        <div className="legal-cards">
          <button className="legal-card" onClick={() => setView('privacy')}>
            <div className="legal-card-icon" style={{ background: '#f0fdf4', color: '#16a34a' }}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 2L3 5v5c0 4.4 3 8.2 7 9 4-0.8 7-4.6 7-9V5L10 2z"/>
              </svg>
            </div>
            <div className="legal-card-text">
              <span className="legal-card-title">Privacy Policy</span>
              <span className="legal-card-sub">How your data is used</span>
            </div>
            <svg className="legal-card-chevron" width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="7 4 13 10 7 16"/>
            </svg>
          </button>

          <button className="legal-card" onClick={() => setView('terms')}>
            <div className="legal-card-icon" style={{ background: '#ede9f4', color: '#55285b' }}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <rect x="4" y="2" width="12" height="16" rx="2"/>
                <line x1="7" y1="7" x2="13" y2="7"/>
                <line x1="7" y1="10" x2="13" y2="10"/>
                <line x1="7" y1="13" x2="10" y2="13"/>
              </svg>
            </div>
            <div className="legal-card-text">
              <span className="legal-card-title">Terms of Use</span>
              <span className="legal-card-sub">Rules for using ChopMyShop</span>
            </div>
            <svg className="legal-card-chevron" width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="7 4 13 10 7 16"/>
            </svg>
          </button>
        </div>

        {/* Quick summary accordion */}
        <button
          className={`legal-summary-toggle${openSummary ? ' open' : ''}`}
          onClick={() => setOpenSummary(o => !o)}
        >
          <span>Quick summary</span>
          <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points={openSummary ? '4 14 10 8 16 14' : '4 6 10 12 16 6'}/>
          </svg>
        </button>

        {openSummary && (
          <ul className="legal-summary-list">
            {QUICK_SUMMARY.map((item, i) => (
              <li key={i} className="legal-summary-item">
                <span className="legal-summary-tick">✓</span>
                {item.text}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
